import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { useLocation } from 'react-router-dom';

import { Grid, Typography, Collapse } from '@mui/material';

import AttendeeDashboardEventCard from '../events/AttendeeDashboardEventCard';
import { getInviteEventId, isCanceledInvite } from './CanceledAttendeeEvents';

const getInviteBookingWhen = (inv) => {
    if (inv?.event && typeof inv.event === 'object') {
        return inv.event.bookingWhen;
    }
    return null;
};

const isPastInvite = (inv, cutoff) => {
    if (!inv?.event || isCanceledInvite(inv)) return false;
    const when = getInviteBookingWhen(inv);
    if (!when) return false;
    return new Date(when) < cutoff;
};

const sortInvitesByDateDesc = (a, b) => {
    const aTime = new Date(getInviteBookingWhen(a) || 0).getTime();
    const bTime = new Date(getInviteBookingWhen(b) || 0).getTime();
    return bTime - aTime;
};

const PastAttendeeEvents = ({ invitedEvents }) => {
    const location = useLocation();
    const [showPastEvents, setShowPastEvents] = useState(false);
    const [userCollapsed, setUserCollapsed] = useState(false);

    const dayBeforeYesterday = new Date();
    dayBeforeYesterday.setDate(dayBeforeYesterday.getDate() - 2);

    const pastInvites = (invitedEvents || [])
        .filter((inv) => isPastInvite(inv, dayBeforeYesterday))
        .sort(sortInvitesByDateDesc);

    const queryEventID = new URLSearchParams(location.search).get('eventID');
    const queryTargetsPast =
        Boolean(queryEventID) &&
        pastInvites.some(
            (inv) => String(getInviteEventId(inv)) === String(queryEventID)
        );
    const pastOpen = userCollapsed
        ? false
        : Boolean(showPastEvents || queryTargetsPast);

    useEffect(() => {
        const eventID = new URLSearchParams(location.search).get('eventID');
        if (!eventID || pastInvites.length === 0) return;
        const targetsPast = pastInvites.some(
            (inv) => String(getInviteEventId(inv)) === String(eventID)
        );
        if (targetsPast) {
            setShowPastEvents(true);
            setUserCollapsed(false);
        }
    }, [location.search, invitedEvents]);

    if (pastInvites.length === 0) {
        return null;
    }

    return (
        <>
            <Typography
                onClick={() => {
                    if (pastOpen) {
                        setUserCollapsed(true);
                        setShowPastEvents(false);
                    } else {
                        setUserCollapsed(false);
                        setShowPastEvents(true);
                    }
                }}
                sx={{
                    color: 'var(--link-color)',
                    cursor: 'pointer',
                    margin: pastOpen
                        ? '16px 16px 0px 16px'
                        : '16px 16px 16px 16px',
                    textAlign: 'center',
                    transition: 'margin 0.3s ease-in-out',
                }}
            >
                {pastOpen ? 'Hide Past Events' : 'Show Past Events'}
            </Typography>
            <Collapse
                in={pastOpen}
                timeout={300}
                sx={{
                    overflow: 'visible',
                    '& .MuiCollapse-wrapper': {
                        overflow: 'visible',
                    },
                    '& .MuiCollapse-wrapperInner': {
                        overflow: 'visible',
                    },
                }}
            >
                <Grid
                    container
                    direction="column"
                    sx={{
                        marginBottom: '20px',
                        marginTop: showPastEvents ? '0px' : '-20px',
                        transition: 'margin 0.3s ease-in-out',
                    }}
                >
                    <Grid item>
                        <Typography component="h2">
                            {pastInvites.length > 1
                                ? `You were invited to these ${pastInvites.length} concerts, in the past`
                                : `You were invited to this concert, in the past`}
                            :
                        </Typography>
                    </Grid>
                    {pastInvites.map(
                        (inv) =>
                            inv?.event && (
                                <AttendeeDashboardEventCard
                                    key={
                                        getInviteEventId(inv) +
                                        (inv.addedAt || '')
                                    }
                                    invite={inv}
                                />
                            )
                    )}
                </Grid>
            </Collapse>
        </>
    );
};

PastAttendeeEvents.propTypes = {
    invitedEvents: PropTypes.array,
};

const mapStateToProps = (state) => ({
    invitedEvents: Array.isArray(state.attendee?.me?.invitedEvents)
        ? state.attendee.me.invitedEvents
        : [],
});

export default connect(mapStateToProps, {})(PastAttendeeEvents);
